import { useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { QRCodeCanvas } from 'qrcode.react'
import { useDbValue } from '../lib/useDbValue.js'
import { advanceBracket, setBracketMatch } from '../lib/data.js'
import { lastNames } from '../lib/teamName.js'
import ScrambleTable from '../leaderboard/ScrambleTable.jsx'

// Match Results — a phone-first winner-fill board, styled to match the iOS
// native app exactly (components/appTheme.ts): forest-green page, ivory cream
// cards with a 1.5px gold border + soft shadow, Georgia serif headings, gold
// diamond dividers, gold flag icons on round headers. Reads every flight's
// bracket live from prod; tap a team + Confirm to set a missing winner; long-
// press a decided match to override. A pick writes to the SAME node the app
// uses — tournaments/{tid}/brackets/{fid}/rounds/{rk}/matches/{mid} = { winnerId }
// — then runs advanceBracket() to fill the next day. No new advancement logic.

const DEFAULT_TID = '-Oxi1JXuaNo3SMh_kqE4' // 62nd Annual (active)

// PBCC premium palette — copied verbatim from the native app's light theme.
const C = {
  green: '#123D26', ink: '#284A38', cream: '#F7F2E6', creamPage: '#EFE7D4',
  gold: '#C2A24E', goldLt: '#E7D6A4', goldText: '#94742A', goldSoft: 'rgba(194,162,78,0.5)',
  muted: '#6B7A6E', onDark: '#FFFFFF', win: '#1F7A44', loss: '#B23A3A',
  selBg: '#E8F5E9', selBorder: '#7BC67E',
}
const SERIF = 'Georgia, "Times New Roman", serif'

const DAYS = [
  { day: 'DAY 1', groups: [{ rk: 'r1', label: 'Round 1' }] },
  { day: 'DAY 2', groups: [{ rk: 'wb2', label: 'Winners' }, { rk: 'cb2', label: 'Consolation' }] },
  { day: 'DAY 3', groups: [
    { rk: 'champ', label: '1st Place' }, { rk: 'third', label: '3rd Place' },
    { rk: 'consf', label: '4th Place' }, { rk: 'fifth', label: '5th Place' },
  ] },
]

const teamName = (t) => lastNames(t?.info?.name) || t?.info?.name || (Array.isArray(t?.info?.players) ? t.info.players.join(' / ') : '') || '—'

// ── Gold golf-flag glyph, the native app's round marker ──────────────────────
function Flag({ size = 15, color = C.gold }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d="M6 22V3" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <path d="M6 3.5 L18 6.5 L6 9.5 Z" fill={color} />
    </svg>
  )
}
// ── Gold diamond divider (mirror of components/GoldDivider) ──────────────────
function Divider() {
  return (
    <div style={S.divRow}>
      <div style={S.divLine} />
      <div style={S.diamond} />
      <div style={S.divLine} />
    </div>
  )
}

export default function ResultsBoard() {
  const { search } = useLocation()
  const tid = new URLSearchParams(search).get('t') || DEFAULT_TID

  const flightsQ = useDbValue(`tournaments/${tid}/flights`)
  const bracketsQ = useDbValue(`tournaments/${tid}/brackets`)
  const flights = flightsQ.data || {}
  const brackets = bracketsQ.data || {}

  const [pending, setPending] = useState(null)      // { fid, rk, mid, teamId }
  const [overrideKey, setOverrideKey] = useState(null) // "fid/rk/mid" unlocked for override
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [showQR, setShowQR] = useState(false)
  const [dayIdx, setDayIdx] = useState(0)           // which day every flight shows (default Day 1)

  const flightIds = useMemo(() => Object.keys(flights).sort(), [flights])

  const tally = useMemo(() => {
    let decided = 0, awaiting = 0
    for (const fid of Object.keys(brackets)) {
      for (const r of Object.values(brackets[fid]?.rounds || {})) {
        for (const m of Object.values(r?.matches || {})) {
          if (m?.winnerId) decided++
          else if (m?.a && m?.b) awaiting++
        }
      }
    }
    return { decided, awaiting }
  }, [brackets])

  async function commit() {
    if (!pending) return
    setBusy(true); setErr(null)
    try {
      await setBracketMatch(tid, pending.fid, pending.rk, pending.mid, { winnerId: pending.teamId, reportedAt: Date.now() })
      await advanceBracket(tid, pending.fid) // fills the next day — same path the app triggers
      setPending(null); setOverrideKey(null)
    } catch (e) {
      setErr(String(e?.message || e))
    } finally {
      setBusy(false)
    }
  }

  const status = flightsQ.status === 'error' || bracketsQ.status === 'error' ? 'error'
    : flightsQ.status === 'loading' || bracketsQ.status === 'loading' ? 'loading' : 'ready'
  const url = `${window.location.origin}/results?t=${tid}`

  return (
    <div style={S.page}>
      {/* Masthead — green with serif white title + gold diamond divider */}
      <div style={S.mast}>
        <div style={S.eyebrowLt}>PINE BLUFF COUNTRY CLUB</div>
        <div style={S.mastTitle}>Match Results</div>
        <Divider />
        <div style={S.mastSub}>Tap a team, then Confirm, to set a winner</div>
        <div style={S.counts}>
          <span style={{ ...S.badge, background: C.win }}>✓ {tally.decided} decided</span>
          <span style={{ ...S.badge, background: 'transparent', border: `1px solid ${C.gold}` }}>{tally.awaiting} awaiting</span>
        </div>
        <div style={S.dayNav}>
          {DAYS.map((d, i) => (
            <button
              key={d.day}
              onClick={() => { setDayIdx(i); setPending(null); setOverrideKey(null) }}
              style={{ ...S.dayTab, ...(dayIdx === i ? S.dayTabOn : null) }}
            >
              {d.day}
            </button>
          ))}
        </div>
        <button style={S.qrBtn} onClick={() => setShowQR((v) => !v)}>{showQR ? 'Hide QR' : 'Open on phone (QR)'}</button>
        {showQR && (
          <div style={S.qrWrap}>
            <div style={S.qrCard}><QRCodeCanvas value={url} size={172} includeMargin /></div>
            <div style={S.qrUrl}>{url}</div>
          </div>
        )}
      </div>

      {status === 'loading' && <div style={S.note}>Loading bracket…</div>}
      {status === 'error' && (
        <div style={S.note}>
          Couldn’t load the bracket.
          <button style={S.retry} onClick={() => { flightsQ.retry(); bracketsQ.retry() }}>Retry</button>
        </div>
      )}

      {status === 'ready' && flightIds.map((fid) => {
        const flight = flights[fid] || {}
        const teams = flight.teams || {}
        const rounds = brackets[fid]?.rounds || {}
        const day = DAYS[dayIdx]
        // Scramble flights (e.g. Flight 12) have no bracket to fill — show the
        // score table instead of match-winner cards.
        if (flight.scramble) {
          return (
            <div key={fid} style={S.flight}>
              <div style={S.flightHead}>
                <Flag size={20} />
                <span style={S.flightName}>{flight.info?.name || fid}</span>
              </div>
              <ScrambleTable data={flight.scramble} />
            </div>
          )
        }
        const visible = day.groups.filter((g) => rounds[g.rk]?.matches)
        return (
          <div key={fid} style={S.flight}>
            <div style={S.flightHead}>
              <Flag size={20} />
              <span style={S.flightName}>{flight.info?.name || fid}</span>
            </div>
            {visible.length === 0 ? (
              <div style={S.emptyDay}>No {day.day} match yet</div>
            ) : visible.map((g) => (
              <div key={g.rk} style={S.group}>
                <div style={S.groupLabel}><Flag size={12} color={C.goldText} /><span>{g.label}</span></div>
                {Object.keys(rounds[g.rk].matches).sort().map((mid) => {
                  const m = rounds[g.rk].matches[mid] || {}
                  const mkey = `${fid}/${g.rk}/${mid}`
                  return (
                    <MatchCard
                      key={mid}
                      m={m}
                      teams={teams}
                      override={overrideKey === mkey}
                      selected={pending && pending.fid === fid && pending.rk === g.rk && pending.mid === mid ? pending.teamId : null}
                      onPick={(teamId) => { setErr(null); setPending({ fid, rk: g.rk, mid, teamId }) }}
                      onLongPress={() => { setErr(null); setOverrideKey(mkey) }}
                    />
                  )
                })}
              </div>
            ))}
          </div>
        )
      })}

      {pending && (
        <ConfirmBar
          label={teamName((flights[pending.fid]?.teams || {})[pending.teamId])}
          busy={busy}
          err={err}
          onConfirm={commit}
          onCancel={() => { setPending(null); setErr(null) }}
        />
      )}
    </div>
  )
}

function MatchCard({ m, teams, selected, override, onPick, onLongPress }) {
  const bothSet = !!m.a && !!m.b
  const decided = !!m.winnerId
  const unlocked = bothSet && (!decided || override) // tappable to pick
  const timer = useRef(null)
  const longFired = useRef(false)

  const startPress = () => {
    if (!decided || override || !bothSet) return
    longFired.current = false
    timer.current = setTimeout(() => { longFired.current = true; onLongPress() }, 500)
  }
  const endPress = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null } }

  const rows = [['a', m.a], ['b', m.b]]
  return (
    <div style={{ ...S.match, ...(override ? S.matchOverride : null) }}>
      {rows.map(([side, teamId], i) => {
        const present = !!teamId
        const isWinner = present && m.winnerId === teamId
        const isSel = present && selected === teamId
        const tappable = unlocked && present
        return (
          <div key={side}>
            {i === 1 && <Divider />}
            <button
              disabled={!tappable}
              onClick={() => { if (longFired.current) { longFired.current = false; return } if (tappable) onPick(teamId) }}
              onTouchStart={startPress} onTouchEnd={endPress} onTouchMove={endPress}
              onMouseDown={startPress} onMouseUp={endPress} onMouseLeave={endPress}
              style={{
                ...S.row,
                ...(isWinner ? S.rowWin : null),
                ...(isSel ? S.rowSel : null),
                ...(!tappable ? S.rowLocked : null),
              }}
            >
              <span style={{ ...S.rowName, ...(isWinner ? S.rowNameWin : null), ...(!present ? S.rowNameTbd : null) }}>
                {present ? teamName(teams[teamId]) : '— TBD —'}
              </span>
              {isWinner && <span style={S.check}>✓</span>}
              {isSel && !isWinner && <span style={S.selDot}>●</span>}
            </button>
          </div>
        )
      })}
      {decided && !override && <div style={S.holdHint}>hold to override</div>}
    </div>
  )
}

function ConfirmBar({ label, busy, err, onConfirm, onCancel }) {
  return (
    <div style={S.confirmWrap}>
      {err && <div style={S.err}>{err}</div>}
      <div style={S.confirmBar}>
        <div style={S.confirmText}>Set <span style={{ fontWeight: 800, color: C.goldLt }}>{label}</span> as the winner?</div>
        <div style={S.confirmBtns}>
          <button style={S.cancel} onClick={onCancel} disabled={busy}>Cancel</button>
          <button style={S.confirm} onClick={onConfirm} disabled={busy}>{busy ? 'Saving…' : 'Confirm'}</button>
        </div>
      </div>
    </div>
  )
}

const cardShadow = '0 6px 12px rgba(0,0,0,0.28)'
const S = {
  page: { maxWidth: 480, margin: '0 auto', padding: '0 14px 150px', background: C.green, minHeight: '100vh', fontFamily: '-apple-system, system-ui, sans-serif', WebkitTextSizeAdjust: '100%' },

  mast: { paddingTop: 20, paddingBottom: 14, textAlign: 'center' },
  eyebrowLt: { fontSize: 11, fontWeight: 800, letterSpacing: 2.5, color: C.goldLt, textTransform: 'uppercase' },
  mastTitle: { fontFamily: SERIF, fontSize: 30, fontWeight: 700, color: C.onDark, letterSpacing: 0.5, marginTop: 4 },
  mastSub: { fontSize: 13, color: 'rgba(255,255,255,0.72)', marginTop: 2 },
  counts: { display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 },
  badge: { fontSize: 12.5, fontWeight: 800, color: C.onDark, borderRadius: 999, padding: '5px 13px', letterSpacing: 0.3 },
  dayNav: { display: 'flex', gap: 6, marginTop: 14, background: 'rgba(0,0,0,0.18)', border: `1.5px solid ${C.gold}`, borderRadius: 16, padding: 4 },
  dayTab: { flex: 1, padding: '11px 0', fontFamily: SERIF, fontSize: 14.5, fontWeight: 700, letterSpacing: 1, color: C.goldLt, background: 'transparent', border: 'none', borderRadius: 12, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' },
  dayTabOn: { background: C.cream, color: C.green, boxShadow: cardShadow },
  qrBtn: { marginTop: 12, width: '100%', padding: '13px', fontFamily: SERIF, fontSize: 15, fontWeight: 700, color: C.green, background: C.cream, border: `1.5px solid ${C.gold}`, borderRadius: 16, boxShadow: cardShadow },
  qrWrap: { marginTop: 12 },
  qrCard: { display: 'inline-block', background: C.cream, border: `1.5px solid ${C.gold}`, borderRadius: 18, padding: 12, boxShadow: cardShadow },
  qrUrl: { fontSize: 11, color: C.goldLt, wordBreak: 'break-all', marginTop: 8 },

  note: { padding: 28, textAlign: 'center', color: C.goldLt, fontFamily: SERIF },
  retry: { marginLeft: 10, padding: '8px 16px', fontWeight: 700, color: C.green, background: C.cream, border: `1.5px solid ${C.gold}`, borderRadius: 12 },

  flight: { marginTop: 22 },
  flightHead: { display: 'flex', alignItems: 'center', gap: 9, padding: '10px 14px', background: C.cream, border: `1.5px solid ${C.gold}`, borderRadius: 16, boxShadow: cardShadow },
  flightName: { fontFamily: SERIF, fontSize: 21, fontWeight: 700, color: C.green },

  emptyDay: { marginTop: 10, padding: '14px', textAlign: 'center', fontFamily: SERIF, fontSize: 14, color: C.muted, background: C.cream, border: `1.5px solid ${C.gold}`, borderRadius: 16 },
  group: { marginTop: 12, marginBottom: 2 },
  groupLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, letterSpacing: 2, color: C.goldLt, textTransform: 'uppercase', margin: '10px 2px 6px' },

  match: { background: C.cream, border: `1.5px solid ${C.gold}`, borderRadius: 20, padding: '4px 6px', marginBottom: 10, boxShadow: cardShadow, position: 'relative' },
  matchOverride: { outline: `2px solid ${C.gold}`, outlineOffset: 1 },
  row: { display: 'flex', alignItems: 'center', width: '100%', minHeight: 54, padding: '12px 14px', border: 'none', background: 'transparent', borderRadius: 14, textAlign: 'left', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' },
  rowWin: { background: C.selBg },
  rowSel: { background: '#fff4d6', outline: `2px solid ${C.gold}` },
  rowLocked: { cursor: 'default' },
  rowName: { flex: 1, fontFamily: SERIF, fontSize: 17, fontWeight: 700, color: C.green },
  rowNameWin: { fontWeight: 800 },
  rowNameTbd: { color: C.muted, fontWeight: 700, letterSpacing: 1 },
  check: { color: C.win, fontWeight: 900, fontSize: 19, marginLeft: 8 },
  selDot: { color: C.gold, fontSize: 13, marginLeft: 8 },
  holdHint: { position: 'absolute', right: 12, bottom: 3, fontSize: 9.5, letterSpacing: 0.5, color: C.muted, textTransform: 'uppercase', fontWeight: 700 },

  // Gold diamond divider
  divRow: { display: 'flex', alignItems: 'center', padding: '0 8px' },
  divLine: { flex: 1, height: 1, background: C.goldSoft },
  diamond: { width: 7, height: 7, background: C.gold, transform: 'rotate(45deg)', margin: '0 11px' },

  confirmWrap: { position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 10 },
  err: { maxWidth: 480, margin: '0 auto', background: C.loss, color: '#fff', fontSize: 13, padding: '8px 14px', textAlign: 'center' },
  confirmBar: { maxWidth: 480, margin: '0 auto', background: C.green, borderTop: `1.5px solid ${C.gold}`, padding: '13px 14px calc(13px + env(safe-area-inset-bottom))', display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 -6px 16px rgba(0,0,0,0.35)' },
  confirmText: { flex: 1, color: C.onDark, fontFamily: SERIF, fontSize: 15 },
  confirmBtns: { display: 'flex', gap: 8 },
  cancel: { padding: '11px 16px', fontSize: 14, fontWeight: 700, color: C.onDark, background: 'transparent', border: '1.5px solid rgba(255,255,255,0.45)', borderRadius: 14 },
  confirm: { padding: '11px 22px', fontFamily: SERIF, fontSize: 14.5, fontWeight: 800, color: C.green, background: C.gold, border: 'none', borderRadius: 14 },
}
